import { useRef, useEffect } from 'react';
import { share } from 'shared-zustand';
import { v4 } from 'uuid';
import { create } from 'zustand';
import { subscribeWithSelector } from '../../node_modules/zustand/esm/middleware.js';
import { shallow as shallow$1 } from '../../node_modules/zustand/esm/shallow.js';
import BulkUploader, { FileStatus } from './uploader.js';
import { ErrorType } from '../errors/errors.types.js';
import { isWoodyError } from '../errors/errors.utils.js';
import { useMutationCreateSubFolder } from '../resources/mutations/useMutationCreateSubFolder.js';
import { useQueryUserInfo } from '../user/queries/useQueryUserInfo.js';
import { useMutationCreateResourceV2 } from '../resources/mutations/useMutationCreateResourceV2.js';
import { ensureError } from '@fabric-app/utils/guards';
import { omit } from '@fabric-app/utils/object/manipulation';
import { handleUpload } from '../upload/utils/handleUpload.js';
import { UploadError } from '../upload/upload.error.js';
import { UploadTaskPriority, UploadErrorCode } from '@fabric-app/types/upload';

const useBulkUploaderStore = create()(subscribeWithSelector((set, get) => ({
  bulkUploader: void 0,
  setBulkUploader: (bulkUploader) => {
    set({
      bulkUploader
    });
  },
  tabId: v4(),
  uploadSpeed: 0,
  setUploadSpeed: (speed) => {
    set({
      uploadSpeed: speed
    });
  },
  sharedFiles: {},
  setSharedFiles: (files) => {
    set({
      sharedFiles: {
        ...get().sharedFiles,
        [get().tabId]: files
      }
    });
  },
  localFiles: [],
  setLocalFiles: (files) => {
    set({
      localFiles: files
    });
  },
  setLocalFile: (file) => {
    const localFiles = get().localFiles;
    const index = localFiles.findIndex((f) => f.id === file.id);
    if (index === -1) {
      set({
        localFiles: [
          ...localFiles,
          file
        ]
      });
    } else {
      set({
        localFiles: localFiles.map((f) => f.id === file.id ? file : f)
      });
    }
  },
  clear: () => {
    set({
      localFiles: [],
      sharedFiles: {}
    });
  }
})));
if ("BroadcastChannel" in globalThis) {
  share("sharedFiles", useBulkUploaderStore);
}
const useSetupBulkUploader = () => {
  const { files, setBulkUploader, setFile, setSharedFiles, setUploadingSpeed } = useBulkUploaderStore((state) => ({
    tabId: state.tabId,
    files: state.localFiles,
    setBulkUploader: state.setBulkUploader,
    setFile: state.setLocalFile,
    setSharedFiles: state.setSharedFiles,
    setUploadingSpeed: state.setUploadSpeed
  }), shallow$1);
  const { data: user } = useQueryUserInfo();
  const userRef = useRef(user);
  userRef.current = user;
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => {
    setSharedFiles(files.map((f) => {
      return omit(f, [
        "abortController"
      ]);
    }));
  }, [
    files,
    setSharedFiles
  ]);
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (filesRef.current?.some((f) => f.status === FileStatus.Uploading || f.status === FileStatus.Ready)) {
        e.preventDefault();
        e.returnValue = "";
      }
      return void 0;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
  const mutationCreateResource = useMutationCreateResourceV2();
  const mutationCreateFolder = useMutationCreateSubFolder();
  const bulkUploaderRef = useRef(new BulkUploader({
    concurrency: 3,
    onFilesAdded: async (items, updateFile) => {
      const paths = /* @__PURE__ */ new Set();
      items.forEach((item) => {
        if (!item.metadata.path || item.metadata.path === "/") return;
        let iteratePath = item.metadata.parent.id + "/" + item.metadata.path;
        while (iteratePath.split("/").length >= 3) {
          paths.add(iteratePath);
          iteratePath = iteratePath.split("/").slice(0, -2).join("/") + "/";
        }
      });
      const sortedPaths = Array.from(paths).sort((a, b) => {
        const aSlashes = a.split("/").length;
        const bSlashes = b.split("/").length;
        return aSlashes - bSlashes;
      });
      const folders = {};
      for (const path of sortedPaths) {
        const splitPath = path.split("/");
        const parentPath = splitPath.slice(0, -2).join("/") + "/";
        const parentFolderId = path.split("/").length === 3 ? splitPath[0] : folders[parentPath].id;
        const baseName = path.split("/").slice(-2, -1)[0];
        const resource = await mutationCreateFolder.mutateAsync({
          folderName: baseName,
          parent: {
            id: parentFolderId,
            name: baseName
          },
          disableToast: true,
          action: "bulk-upload"
        });
        folders[path] = {
          id: resource.id,
          name: baseName
        };
      }
      const newItems = items.map((item) => {
        if (!item.metadata.path || item.metadata.path === "/") return item;
        const fullPath = item.metadata.parent.id + "/" + item.metadata.path;
        const parentFolder = folders[fullPath];
        if (!parentFolder) return item;
        return {
          ...item,
          metadata: {
            ...item.metadata,
            parent: parentFolder
          },
          processed: true
        };
      });
      newItems.forEach((item) => updateFile(item));
    },
    uploadFunction: async (item, onProgressUpdate, toggleFinalizing, signal, options) => {
      try {
        const { key, file } = await handleUpload({
          data: {
            type: "file",
            file: item.file,
            available: true
          },
          userTier: userRef.current?.subscription.tier,
          abortSignal: signal,
          onProgress: ({ overall, uploadSpeed }) => {
            onProgressUpdate(overall, uploadSpeed);
          },
          bucket: "stored-files",
          // so it has more priority than e.g. screenshots which are running in the BG
          priority: UploadTaskPriority.Medium
        });
        toggleFinalizing();
        const { parent, comment, tags } = item.metadata;
        const thumbnailUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : void 0;
        const resource = await mutationCreateResource.mutateAsync({
          data: {
            createType: "file",
            body: {
              attachment: {
                path: key,
                filename: file.name
              },
              mimeType: file.type,
              name: null,
              parentId: parent.id,
              tags,
              comment: comment ? {
                content: comment
              } : void 0
            },
            optimisticData: {
              file,
              tags,
              parent,
              thumbnailUrl
            }
          },
          disableToast: options?.disableToast ?? true,
          action: "bulk-upload"
        });
        return resource;
      } catch (error) {
        console.error("Error uploading file", error);
        if (error instanceof UploadError) {
          throw ensureError(error);
        }
        if (isWoodyError(error) && error.detail === ErrorType.EXCEEDS_STORAGE_LIMIT) {
          throw new UploadError(UploadErrorCode.QuotaExceeded, "You have exceeded your storage limit.", false);
        } else if (isWoodyError(error) && error.detail === ErrorType.EXCEEDS_ITEM_LIMIT) {
          throw new UploadError(UploadErrorCode.QuotaExceeded, "You have exceeded your item limit.", false);
        }
        throw new UploadError(UploadErrorCode.FileUploadFailed);
      }
    },
    onFileUpdate: (file) => {
      setFile(file);
    },
    onUploadComplete: (file, response, updateFile) => {
      const newFile = {
        ...file,
        metadata: {
          ...file.metadata,
          fdocId: response.id
        }
      };
      updateFile(newFile);
      setFile(newFile);
    },
    onSpeedUpdate: (speed) => {
      setUploadingSpeed(speed);
    }
  }));
  useEffect(() => {
    setBulkUploader(bulkUploaderRef.current);
  }, [
    setBulkUploader
  ]);
};

export { useBulkUploaderStore as default, useSetupBulkUploader };
//# sourceMappingURL=store.js.map
